/* dc-delivery.js — fecha de entrega dinámica (defer)
 *
 * Las fechas se calculan aquí y NO en Liquid: Shopify cachea el HTML renderizado, así
 * que una fecha resuelta en el servidor se congelaría con el caché y la clienta que
 * entra el martes vería la del lunes. Misma restricción que dc-countdown.
 *
 * El archivo se puede cargar en Node para testear el motor de calendario: las funciones
 * puras se exportan bajo guarda de `module` y el registro del custom element solo corre
 * si existe `customElements`.
 */
(() => {
  'use strict';

  const DAY = 864e5;

  // Festivos de Colombia 2026–2028: fijos + Ley Emiliani (los movibles se corren al
  // lunes siguiente) + los ligados a la Pascua. Ya resueltos, para no llevar el cálculo
  // de la Pascua al navegador. Dieciocho por año.
  const CO_HOLIDAYS = [
    '2026-01-01', '2026-01-12', '2026-03-23', '2026-04-02', '2026-04-03', '2026-05-01',
    '2026-05-18', '2026-06-08', '2026-06-15', '2026-06-29', '2026-07-20', '2026-08-07',
    '2026-08-17', '2026-10-12', '2026-11-02', '2026-11-16', '2026-12-08', '2026-12-25',
    '2027-01-01', '2027-01-11', '2027-03-22', '2027-03-25', '2027-03-26', '2027-05-01',
    '2027-05-10', '2027-05-31', '2027-06-07', '2027-07-05', '2027-07-20', '2027-08-07',
    '2027-08-16', '2027-10-18', '2027-11-01', '2027-11-15', '2027-12-08', '2027-12-25',
    '2028-01-01', '2028-01-10', '2028-03-20', '2028-04-13', '2028-04-14', '2028-05-01',
    '2028-05-29', '2028-06-19', '2028-06-26', '2028-07-03', '2028-07-20', '2028-08-07',
    '2028-08-21', '2028-10-16', '2028-11-06', '2028-11-13', '2028-12-08', '2028-12-25',
  ];

  const pad = (n) => (n < 10 ? '0' : '') + n;
  const key = (t) => {
    const d = new Date(t);
    return d.getUTCFullYear() + '-' + pad(d.getUTCMonth() + 1) + '-' + pad(d.getUTCDate());
  };
  const dow = (t) => new Date(t).getUTCDay();

  function isBusinessDay(t, cfg) {
    if (!cfg.workdays[dow(t)]) return false;
    return !cfg.holidays.has(key(t));
  }

  // El guard corta el bucle si el admin desmarca los siete días: sin él la página se
  // congela. readConfig también valida, pero esto protege al motor por su cuenta.
  function nextBusinessDay(t, cfg) {
    let cur = t + DAY;
    for (let i = 0; i < 400 && !isBusinessDay(cur, cfg); i++) cur += DAY;
    return cur;
  }

  // Avanza n días hábiles. Con n = 0 no avanza, pero si el día de partida no es hábil
  // se corre igual al siguiente que sí lo sea: ningún resultado cae en fin de semana.
  function addBusinessDays(t, n, cfg) {
    let cur = t;
    for (let i = 0; i < n; i++) cur = nextBusinessDay(cur, cfg);
    for (let i = 0; i < 400 && !isBusinessDay(cur, cfg); i++) cur += DAY;
    return cur;
  }

  // Regla:
  //   dia_base = hoy; si hoy es hábil y ya pasó el corte, dia_base = siguiente hábil
  //   despacho = dia_base + dispatchDays hábiles
  //   entrega  = despacho + transitMin / transitMax
  //
  // El corte solo mueve el día base en días hábiles: un domingo a las 11 de la noche no
  // "pasa el corte", porque ese domingo no había despacho que perder.
  function computeDates(now, cfg) {
    const pastCutoff = (now.hour * 60 + now.minute) >= (cfg.cutoffHour * 60 + cfg.cutoffMinute);
    let base = now.t;
    if (isBusinessDay(now.t, cfg) && pastCutoff) base = nextBusinessDay(now.t, cfg);

    const dispatch = addBusinessDays(base, cfg.dispatchDays, cfg);
    const step = cfg.transitBusiness
      ? (t, n) => addBusinessDays(t, n, cfg)
      : (t, n) => t + n * DAY;

    const lo = Math.min(cfg.transitMin, cfg.transitMax);
    const hi = Math.max(cfg.transitMin, cfg.transitMax);

    return {
      today: now.t,
      base,
      dispatch,
      min: step(dispatch, lo),
      max: step(dispatch, hi),
      pastCutoff,
    };
  }

  // Nombres propios en vez de Intl: las abreviaturas de Intl varían entre navegadores
  // ("ago" / "ago." / "agosto") y aquí el formato tiene que coincidir con el diseño.
  const MES = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto',
    'septiembre', 'octubre', 'noviembre', 'diciembre'];
  const MESC = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
  const DIA = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
  const DIAC = ['dom', 'lun', 'mar', 'mié', 'jue', 'vie', 'sáb'];

  const nd = (t) => new Date(t).getUTCDate();
  const nm = (t) => new Date(t).getUTCMonth();

  const fLong = (t) => DIA[dow(t)] + ' ' + nd(t) + ' de ' + MES[nm(t)];
  const fShort = (t) => DIAC[dow(t)] + ' ' + nd(t) + ' ' + MESC[nm(t)];
  const fDay = (t) => nd(t) + ' de ' + MES[nm(t)];
  const fDayS = (t) => nd(t) + ' ' + MESC[nm(t)];

  function fRange(min, max) {
    if (key(min) === key(max)) return 'el ' + fDay(min);
    if (nm(min) === nm(max)) return 'entre el ' + nd(min) + ' y el ' + fDay(max);
    return 'entre el ' + fDay(min) + ' y el ' + fDay(max);
  }

  function fRangeShort(min, max) {
    if (key(min) === key(max)) return fDayS(min);
    if (nm(min) === nm(max)) return nd(min) + ' – ' + fDayS(max);
    return fDayS(min) + ' – ' + fDayS(max);
  }

  function fClock(ms) {
    const total = Math.max(0, Math.floor(ms / 60000));
    const h = Math.floor(total / 60);
    const m = total % 60;
    if (h > 0) return h + ' h ' + m + ' min';
    if (total > 0) return total + ' min';
    return 'menos de 1 min';
  }

  const ESCAPES = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
  const esc = (s) => String(s).replace(/[&<>"']/g, (c) => ESCAPES[c]);

  // `escapeTpl` es true para los ajustes de tipo `text` (el admin escribe texto plano) y
  // false para los `richtext`, que ya vienen con HTML del editor. Los valores que se
  // inyectan van escapados siempre; solo `{rango}` genera marcado, y lo genera esta
  // función, no el admin.
  function fill(tpl, vals, escapeTpl) {
    const base = escapeTpl ? esc(tpl) : String(tpl);
    return base.replace(/\{(\w+)\}/g, (m, k) => {
      if (!(k in vals)) return m;
      const v = esc(vals[k]);
      return k === 'rango' ? '<em class="dc-del__hl">' + v + '</em>' : v;
    });
  }

  function tokens(d, cfg, clockMs) {
    const lo = Math.min(cfg.transitMin, cfg.transitMax);
    const hi = Math.max(cfg.transitMin, cfg.transitMax);
    return {
      rango: fRange(d.min, d.max),
      min: fDay(d.min),
      max: fDay(d.max),
      despacho: fLong(d.dispatch),
      hoy: fLong(d.today),
      dias: lo === hi ? String(lo) : lo + ' a ' + hi,
      reloj: clockMs == null ? '' : fClock(clockMs),
    };
  }

  // Devuelve la fecha civil de la zona como milisegundos UTC (para que toda la
  // aritmética de días sea exacta) más la hora local de esa zona. Así el horario de
  // verano del visitante y su getTimezoneOffset dejan de importar.
  function nowInZone(tz, date) {
    const d = date || new Date();
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric', month: '2-digit', day: '2-digit',
        hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
      }).formatToParts(d).reduce((acc, p) => { acc[p.type] = p.value; return acc; }, {});
      // hour12:false devuelve "24" para la medianoche en algunos motores.
      const hour = parseInt(parts.hour, 10) % 24;
      return {
        t: Date.UTC(+parts.year, +parts.month - 1, +parts.day),
        hour,
        minute: +parts.minute,
        second: +parts.second,
      };
    } catch (e) {
      // Zona desconocida o Intl sin soporte de timeZone: hora local del dispositivo.
      return {
        t: Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()),
        hour: d.getHours(), minute: d.getMinutes(), second: d.getSeconds(),
      };
    }
  }

  function msToCutoff(now, cfg) {
    const cut = (cfg.cutoffHour * 60 + cfg.cutoffMinute) * 60000;
    const cur = (now.hour * 60 + now.minute) * 60000 + (now.second || 0) * 1000;
    return cut - cur;
  }

  const API = {
    CO_HOLIDAYS, isBusinessDay, nextBusinessDay, addBusinessDays, key, computeDates,
    fLong, fShort, fDay, fDayS, fRange, fRangeShort, fClock, fill, tokens,
    nowInZone, msToCutoff,
  };

  if (typeof module === 'object' && module.exports) module.exports = API;

  // A partir de aquí, solo navegador.
  if (typeof customElements === 'undefined') return;

  const LUN_A_VIE = [false, true, true, true, true, true, false];

  function readConfig(el) {
    const d = el.dataset;
    const int = (v, dflt) => { const n = parseInt(v, 10); return isNaN(n) ? dflt : n; };

    const marcados = String(d.workdays || '').split(',').map((x) => x.trim() === '1');
    const workdays = (marcados.length === 7 && marcados.some(Boolean)) ? marcados : LUN_A_VIE;

    const holidays = new Set();
    if (d.holidaysCo !== 'false') CO_HOLIDAYS.forEach((k) => holidays.add(k));
    String(d.holidaysExtra || '').split(/[\s,;]+/).forEach((k) => {
      if (/^\d{4}-\d{2}-\d{2}$/.test(k)) holidays.add(k);
    });

    // La tabla llega hasta 2028. Vencida, la sección sigue funcionando solo con los
    // fines de semana —nunca deja de renderizar— pero deja constancia de que hay que
    // ampliarla, porque a partir de ahí puede prometer una entrega en festivo.
    if (d.holidaysCo !== 'false') {
      const ultimo = parseInt(CO_HOLIDAYS[CO_HOLIDAYS.length - 1].slice(0, 4), 10);
      if (new Date().getFullYear() > ultimo) {
        console.warn('dc-delivery: la tabla de festivos termina en ' + ultimo
          + '. Amplíala en assets/dc-delivery.js o carga las fechas en el ajuste "Festivos propios".');
      }
    }

    return {
      tz: d.tz || 'America/Bogota',
      cutoffHour: Math.min(23, Math.max(0, int(d.cutoffHour, 17))),
      cutoffMinute: Math.min(59, Math.max(0, int(d.cutoffMinute, 0))),
      workdays,
      holidays,
      dispatchDays: Math.max(0, int(d.dispatchDays, 1)),
      transitMin: Math.max(0, int(d.transitMin, 2)),
      transitMax: Math.max(0, int(d.transitMax, 3)),
      transitBusiness: d.transitMode !== 'calendar',
      clock: d.clock !== 'false',
      clockThreshold: Math.max(0, int(d.clockThreshold, 8)),
    };
  }

  class DCDelivery extends HTMLElement {
    connectedCallback() {
      this.cfg = readConfig(this);
      this.paint();
      // Un tick por segundo: barato, y hace que al cruzarse el corte las fechas se
      // recalculen solas sin que la clienta tenga que recargar.
      this.timer = setInterval(() => this.paint(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    paint() {
      const cfg = this.cfg;
      const now = nowInZone(cfg.tz);
      const d = computeDates(now, cfg);
      const falta = msToCutoff(now, cfg);
      const relojOn = cfg.clock
        && isBusinessDay(now.t, cfg)
        && falta > 0
        && falta <= cfg.clockThreshold * 3600000;
      const vals = tokens(d, cfg, relojOn ? falta : null);

      // Repintar solo cuando cambie algo visible: son 60 repintados por minuto.
      const sig = [d.dispatch, d.min, d.max, relojOn, vals.reloj].join('|');
      if (sig === this._sig) return;
      this._sig = sig;

      this.querySelectorAll('[data-dc-del-tpl]').forEach((el) => {
        const esRich = el.hasAttribute('data-dc-del-raw');
        el.innerHTML = fill(el.getAttribute('data-dc-del-tpl') || '', vals, !esRich);
      });

      const reloj = this.querySelector('[data-dc-del-clock]');
      if (reloj) {
        const tpl = (relojOn ? reloj.getAttribute('data-tpl-in') : reloj.getAttribute('data-tpl-out')) || '';
        reloj.innerHTML = fill(tpl, vals, true);
        reloj.hidden = !tpl.trim();
        // Al cambiar de minuto cruza un brillo por la pastilla: es lo que hace que la
        // cuenta atrás se lea como algo que corre y no como un texto fijo. Se salta el
        // primer pintado, que ocurre antes de que la sección llegue a pantalla.
        // Quitar la clase y forzar un reflow es lo que reinicia la animación.
        if (this._pintado && !reloj.hidden) {
          reloj.classList.remove('is-tick');
          void reloj.offsetWidth;
          reloj.classList.add('is-tick');
        }
      }

      this.querySelectorAll('[data-dc-del-step]').forEach((step) => {
        const out = step.querySelector('[data-dc-del-date]');
        if (!out) return;
        const role = step.getAttribute('data-role') || 'hoy';
        const fmt = step.getAttribute('data-format') || 'auto';
        const prefix = (step.getAttribute('data-prefix') || '').trim();
        const t = role === 'despacho' ? d.dispatch : role === 'entrega' ? d.min : d.today;

        let txt;
        if (role === 'entrega') {
          txt = fmt === 'largo' ? fRange(d.min, d.max) : fRangeShort(d.min, d.max);
        } else if (fmt === 'largo') {
          txt = fLong(t);
        } else if (fmt === 'sindia') {
          txt = fDayS(t);
        } else if (fmt === 'corto') {
          txt = fShort(t);
        } else {
          // auto: con prefijo ("Hoy") el día de la semana sobra y suena redundante.
          txt = prefix ? fDayS(t) : fShort(t);
        }

        out.textContent = prefix ? prefix + ' ' + txt : txt;
        out.setAttribute('datetime', key(t));
        out.hidden = false;
      });

      this._pintado = true;
    }
  }

  if (!customElements.get('dc-delivery')) customElements.define('dc-delivery', DCDelivery);
})();
